import React, { ReactNode, FC, useCallback, useMemo } from 'react';
import { DropzoneInputProps, DropzoneRootProps, useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';

import { WithTranslation } from 'react-i18next';
import openModalByName from '../../../actions/modal-fn/openModalByName';
import PrimaryActionButton from '../../buttons/PrimaryActionButton';
import ErrorModal from '../../modal/ErrorModal';

import UploadHint from './UploadHint';
import UploadError from './UploadError';

import { StyledActionBar } from './Upload.styles';

export interface UploadRenderProps {
  targetProps: DropzoneRootProps;
  inputProps: DropzoneInputProps;
  accept: string;
}

export interface UploadProps {
  children: (renderProps: UploadRenderProps) => ReactNode;
  /** Maximum size in bytes. */
  maxSize?: number;
  /** File types accepted, see [accept](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept).  */
  accept?: string | string[];
  /** Remove the type and size hint.  */
  noHint?: boolean;
  /** Allows multiple files to be selected. */
  multiple?: boolean;
  /** Message shown in the modal when the file is too large */
  fileTooLargeMessage?: string;
  /** Message shown in the modal when the file type is invalid */
  fileInvalidTypeMessage?: string;
  /** Callback when a file is added. */
  onValueChange?: (files: File[]) => void;
  /** Callback when the upload was clicked but no files were selected.  */
  onTouched?: () => void;
  /** @ignore */
  disabled?: boolean;
  /** @ignore */
  uploadId?: number;
}

const UploadDefaultProps: Partial<UploadProps> = {
  multiple: true,
  noHint: false,
  maxSize: 20971520, // 20MB
};

const Upload: FC<UploadProps & WithTranslation> = ({
  t, children, uploadId, disabled, noHint, multiple, accept, maxSize, onValueChange, onTouched, fileTooLargeMessage, fileInvalidTypeMessage, ...props
}) => {
  const dispatch = useDispatch();
  const modalName = useMemo(() => `Upload${uploadId}Modal`, [uploadId]);

  const resolvedAccept = useMemo(() => (
    Array.isArray(accept) ? accept.join(',') : accept
  ), [accept]);

  const handleDrop = useCallback((files: File[]) => {
    onValueChange(files);
  }, [onValueChange]);

  const handleRejected = useCallback(() => {
    dispatch(openModalByName(modalName));
  }, [dispatch, modalName]);

  const handleCancel = useCallback(() => {
    if (onTouched) {
      onTouched();
    }
  }, [onTouched]);

  const dropZoneOptions = useMemo(() => ({
    onDrop: handleDrop,
    onDropRejected: handleRejected,
    onFileDialogCancel: handleCancel,
    accept: resolvedAccept,
    multiple,
    disabled,
    maxSize,
  }), [resolvedAccept, disabled, handleCancel, handleDrop, handleRejected, maxSize, multiple]);

  const { getRootProps, getInputProps, fileRejections } = useDropzone(dropZoneOptions);

  const tabIndexProps = useMemo(() => disabled ? { tabIndex: -1 } : {}, [disabled]);

  const renderProps = useMemo(() => ({
    targetProps: {
      ...props,
      ...getRootProps(),
      ...tabIndexProps,
    },
    inputProps: getInputProps(),
    accept: resolvedAccept,
  }), [getInputProps, getRootProps, props, resolvedAccept, tabIndexProps]);

  return (
    <>
      {!noHint && <UploadHint accept={resolvedAccept} maxSize={maxSize} />}
      {children(renderProps)}
      <ErrorModal name={modalName}>
        <UploadError
          errorType="file-too-large"
          fileRejections={fileRejections}
          title={fileTooLargeMessage}
        />
        <UploadError
          errorType="file-invalid-type"
          fileRejections={fileRejections}
          title={fileInvalidTypeMessage}
        />
        <UploadError
          errorType="too-many-files"
          fileRejections={fileRejections}
          title={fileInvalidTypeMessage}
        />
        <ErrorModal.Footer>
          <StyledActionBar nopadding>
            <PrimaryActionButton
              data-testid="UploadOkButton"
              onClickAction={`${modalName}.closeModal`}
            >{t('okButton')}
            </PrimaryActionButton>
          </StyledActionBar>
        </ErrorModal.Footer>
      </ErrorModal>
    </>
  );
};

Upload.defaultProps = UploadDefaultProps;

export default Upload;
